import { buyerBudgets, Prisma } from '@prisma/client';
import axiosInstance from '../api';

export interface BuyerBudgetData {
  maxPrice: number;
  monthlyPayment: number;
  downPayment: number;
  annualIncome?: number;
  monthlyDebts?: number;
  currentSavings?: number;
  creditScoreRange?: string;
  profile?: string;
  preApproved?: boolean;
  preApprovalAmount?: number;
  limitedByDownPayment?: boolean;
  potentialMaxPrice?: number;
}

const buyerBudgetsService = {
  // Get the current user's budget
  find: async (): Promise<buyerBudgets | null> => {
    try {
      const res = await axiosInstance.get<buyerBudgets>('/buyer-budgets');
      // If backend returns empty object, return null
      if (res.data && Object.keys(res.data).length === 0) {
        return null;
      }
      return res.data;
    } catch (error: any) {
      // If no budget exists, return null instead of throwing
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // Create a new budget for the user
  create: async (data: BuyerBudgetData): Promise<buyerBudgets> => {
    const res = await axiosInstance.post<buyerBudgets>('/buyer-budgets', data);
    return res.data;
  },

  // Update an existing budget
  update: async (id: string, data: Partial<BuyerBudgetData>): Promise<buyerBudgets> => {
    const res = await axiosInstance.patch<buyerBudgets>(`/buyer-budgets/${id}`, data);
    return res.data;
  },

  // Save budget (creates or updates as needed)
  save: async (data: BuyerBudgetData): Promise<buyerBudgets> => {
    try {
      // Try to get existing budget
      const existing = await buyerBudgetsService.find();
      if (existing?.id) {
        // Update existing budget
        return await buyerBudgetsService.update(existing.id, data);
      } else {
        // Create new budget
        return await buyerBudgetsService.create(data);
      }
    } catch (error) {
      console.error('Error saving budget:', error);
      throw error;
    }
  },

  // Delete a budget
  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/buyer-budgets/${id}`);
  },
};

export default buyerBudgetsService;